import {
  useDeleteApprovalPolicyMutation,
  workspaceApprovalPoliciesQuery,
} from "@common/api/approval-policies"
import { ConfirmDialog } from "@common/components/shared/confirm-dialog"
import { handleError } from "@common/utils"
import { useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router"
import useCustomToast from "@/hooks/use-custom-toast"
import { requirePermission } from "@/lib/require-permission"
import { DELETE_FLOWS_PERMISSION } from "../-lib/permission"

export const Route = createFileRoute(
  "/_layout/settings/_panel/workspace/approval-flows/$policyId/delete",
)({
  beforeLoad: ({ context: { queryClient } }) =>
    requirePermission(
      queryClient,
      DELETE_FLOWS_PERMISSION,
      "/settings/workspace/general",
    ),
  loader: async ({ context: { queryClient }, params: { policyId } }) => {
    const policies = await queryClient.ensureQueryData(
      workspaceApprovalPoliciesQuery(),
    )
    const entry = policies.policies.find(
      (candidate) => candidate.policy.id === policyId,
    )
    if (!entry || entry.policy.is_default) {
      throw redirect({
        to: "/settings/workspace/approval-flows/$policyId",
        params: { policyId },
        replace: true,
      })
    }
  },
  component: DeletePolicyDialog,
})

function DeletePolicyDialog(): React.ReactElement | null {
  const { policyId } = Route.useParams()
  const navigate = useNavigate()
  const showToast = useCustomToast()
  const mutation = useDeleteApprovalPolicyMutation()
  const { data } = useSuspenseQuery(workspaceApprovalPoliciesQuery())
  const entry = data.policies.find(
    (candidate) => candidate.policy.id === policyId,
  )

  if (!entry || entry.policy.is_default) {
    return null
  }

  const { policy } = entry

  const close = () =>
    navigate({
      to: "/settings/workspace/approval-flows/$policyId",
      params: { policyId },
    })

  const confirm = () =>
    mutation.mutate(
      { policy_id: policyId },
      {
        onSuccess: () => {
          showToast("Deleted", `${policy.name} deleted.`, "success")
          navigate({
            to: "/settings/workspace/approval-flows",
            search: { subject: policy.subject },
            ignoreBlocker: true,
          })
        },
        onError: (error) => handleError(error, showToast),
      },
    )

  return (
    <ConfirmDialog
      title="Delete this flow?"
      description={`New approvals stop using ${policy.name}; the next matching flow, or the default, takes over. Approvals already under way keep their steps. Turn it off instead if you might want it back.`}
      action="Delete flow"
      pendingAction="Deleting…"
      pending={mutation.isPending}
      onConfirm={confirm}
      onCancel={close}
    />
  )
}
